import { Divider } from "@mui/material";
import { useIntl } from "react-intl";

import { Skeleton } from "@/components/Skeleton/Skeleton";
import {
  DetailsList,
  DetailsListItem,
} from "@/features/trips/components/DetailsList";
import { ImageCarousel } from "@/features/trips/components/ImageCarousel";
import { TripTag } from "@/features/trips/components/TripTag";
import type { TripDetails } from "@/features/trips/types/types";

type Props = {
  trip: TripDetails;
};

export function OverviewCard({ trip }: Props) {
  const intl = useIntl();

  return (
    <div className="rounded-3xl bg-white p-1">
      <ImageCarousel
        imagesUrls={trip.imageUrls || []}
        imageRatio="3/2"
        tags={trip.tags.map((tag) => (
          <TripTag key={tag.id} title={tag.name} />
        ))}
      />

      <div className="p-3">
        <div className="mb-1 text-xl font-medium text-text-title">
          {trip.name}
        </div>
        <div className="text-sm text-text-tertiary">{trip.subtitle}</div>

        <Divider
          sx={{
            borderColor: "var(--color-stroke-neutral-softer)",
            my: "calc(var(--spacing)*3)",
          }}
        />

        <DetailsList>
          <DetailsListItem
            title={intl.formatMessage({
              defaultMessage: "Travel length",
              description: "trips.travelLength",
            })}
            value={intl.formatMessage(
              {
                defaultMessage:
                  "{nights} {nights, plural, one {night} other {nights}} / {days} {days, plural, one {day} other {days}}",
                description: "trips.duration",
              },
              {
                nights: trip.durationInDays - 1,
                days: trip.durationInDays,
              },
            )}
          />

          <DetailsListItem
            title={intl.formatMessage({
              defaultMessage: "Flight operator",
              description: "trips.flightOperator",
            })}
            value={trip.airline}
          />
        </DetailsList>

        <div className="mt-5 text-sm">{trip.description}</div>
      </div>
    </div>
  );
}

export const OverviewCardSkeleton = () => {
  return (
    <div className="rounded-3xl bg-white p-1">
      <div className="flex aspect-3/2 shrink-0 overflow-clip rounded-t-2xl">
        <Skeleton width="100%" height="100%" variant="rectangular" />
      </div>

      <div className="p-4">
        <div className="mb-2">
          <Skeleton width="75%" height={28} />
        </div>
        <Skeleton width="50%" height={20} />

        <Divider
          sx={{
            borderColor: "var(--color-stroke-neutral-softer)",
            my: "calc(var(--spacing)*3)",
          }}
        />

        <div className="mb-5 grid grid-cols-2 grid-rows-2 gap-x-2 gap-y-0.5">
          <Skeleton width="50%" height={20} />
          <Skeleton width="50%" height={20} />
          <Skeleton width="40%" height={20} />
          <Skeleton width="20%" height={20} />
        </div>

        <Skeleton width="100%" height={40} />
      </div>
    </div>
  );
};
